import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFileCsv, FaFileExcel, FaSpinner, FaFileImport } from "react-icons/fa";
import { apiService } from "../Config/api.service";
import * as XLSX from "xlsx";
import ImportMappingModal from "./ImportMappingModal";
import ExportModal from "./ExportModal";
import { notify } from "../Utils/Notifications";

function ImportExportButtons({
    model_name,
    filename = "export",
    domain = [],
    ids = [],
    importUrl,
    onImportSuccess,
    dropdown,
    accept = ".csv,.xls,.xlsx",
    exampleUrl = null
}) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    // NOUVEAU : État pour le loader dans la modale de mapping
    const [isSubmittingMapping, setIsSubmittingMapping] = useState(false);
    const fileInputRef = useRef();
    const [fileData, setFileData] = useState(null);
    const [fileColumns, setFileColumns] = useState([]);
    const [showMapping, setShowMapping] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [serverFields, setServerFields] = useState([]);
    const [importErrors, setImportErrors] = useState(null);

    // NOUVEAU : États pour la sélection de feuille Excel
    const [sheetNames, setSheetNames] = useState([]);
    const [currentWorkbook, setCurrentWorkbook] = useState(null);
    const [showSheetSelection, setShowSheetSelection] = useState(false);




    const handleExport = async (selectedFields, format) => {
        setLoading(true);
        try {
            await apiService.exportData({
                model_name,
                fields: selectedFields, 
                domain,
                ids: ids || [],
                format,
                filename
            });
            notify(t('importExportButtons.exportSuccess'), null, 2);
        } catch (err) {
            notify(err.message || t('importExportButtons.exportError'), null, 3);
        }
        setLoading(false);
    };

    const handleImportClick = () => {
        fileInputRef.current && fileInputRef.current.click();
        getServerFields();
    };

    // NOUVEAU : Fonction pour traiter une feuille spécifique
    const processSheetData = (workbook, sheetName) => {
        try {
            const sheet = workbook.Sheets[sheetName];
            const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            
            if (!json || json.length === 0) {
                throw new Error(t('importExportButtons.selectedSheetEmpty'));
            }

            const columns = json[0];
            const rows = json.slice(1).map(row =>
                Object.fromEntries(columns.map((col, i) => [col, row[i]]))
            );
            setFileColumns(columns);
            setFileData(rows);
            setShowMapping(true);
        } catch (err) {
            notify(err.message || t('importExportButtons.sheetReadError'), null, 3);
        }
    };

    // NOUVEAU : Handler lors du clic sur une feuille dans la modale
    const handleSheetSelect = (name) => {
        processSheetData(currentWorkbook, name);
        setShowSheetSelection(false);
        setCurrentWorkbook(null);
        setSheetNames([]);
    };

    const handleFileChange = async (e) => {
        if (e.target.files.length > 0) {
            setImporting(true);
            const file = e.target.files[0];
            try {
                let rows = [];
                if (file.name.endsWith('.csv')) {
                    const text = await file.text();
                    const lines = text.split('\n').filter(Boolean);
                    const columns = lines[0].split(';');
                    rows = lines.slice(1).map(line => {
                        const values = line.split(';');
                        return Object.fromEntries(columns.map((col, i) => [col.trim(), values[i]?.trim()]));
                    });
                    setFileColumns(columns);
                    setFileData(rows);
                    setShowMapping(true);
                } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
                    const data = await file.arrayBuffer();
                    const workbook = XLSX.read(data, { type: 'array' });
                    
                    if (workbook.SheetNames.length > 1) {
                        // Plusieurs feuilles : on demande à l'utilisateur
                        setCurrentWorkbook(workbook);
                        setSheetNames(workbook.SheetNames);
                        setShowSheetSelection(true);
                        // On ne lance pas le mapping tout de suite
                    } else if (workbook.SheetNames.length === 1) {
                        // Une seule feuille : on traite directement
                        processSheetData(workbook, workbook.SheetNames[0]);
                    } else {
                        throw new Error(t('importExportButtons.noSheetInFile'));
                    }
                }
            } catch (err) {
                notify(err.message || t('importExportButtons.fileReadError'), null, 3);
            }
            setImporting(false);
            e.target.value = null;
        }
    };


    const generateDemoRows = (fields, modelName) => {
        const getDemoValue = (field, index) => {
            const fName = (field.name || field || "").toString().toLowerCase();
            const fLabel = (field.string || "").toString().toLowerCase();
            const fType = field.type || "char";
            const selection = field.selection;

            if (fName === "gender" || fLabel.includes("genre") || fLabel.includes("sexe")) {
                return index === 1 ? "Homme" : "Femme";
            }
            if (fName === "marital" || fLabel.includes("matrimonial")) {
                return index === 1 ? "Célibataire" : "Marié";
            }
            if (fName === "state" || fLabel.includes("statut") || fLabel.includes("état")) {
                return index === 1 ? "En cours" : "Brouillon";
            }

            if (fType === "boolean") {
                return index === 1 ? "Non" : "Oui";
            }

            if (fType === "selection" && Array.isArray(selection) && selection.length > 0) {
                const opt = selection[(index - 1) % selection.length];
                const val = Array.isArray(opt) ? (opt[1] || opt[0]) : opt;
                return val;
            }

            if (fName === "identification_id" || fName === "matricul" || fName === "identifiant") {
                return index === 1 ? "EMP001" : "EMP002";
            }
            if (fName === "name") {
                if (modelName === "employee" || modelName === "hr.employee") {
                    return index === 1 ? "Jean Dupont" : "Marie Martin";
                }
                if (modelName === "contract" || modelName === "hr.contract") {
                    return index === 1 ? "CDI - Jean Dupont" : "CDD - Marie Martin";
                }
                if (modelName === "prime" || modelName === "excohsp.regle") {
                    return index === 1 ? "Prime de Rendement" : "Indemnité de Logement";
                }
                return index === 1 ? "Exemple 1" : "Exemple 2";
            }
            if (fName.includes("email")) {
                return index === 1 ? "jean.dupont@example.com" : "marie.martin@example.com";
            }
            if (fName.includes("phone")) {
                return index === 1 ? "+33 6 12 34 56 78" : "+33 6 98 76 54 32";
            }
            if (fName.includes("job") || fLabel.includes("poste")) {
                return index === 1 ? "Développeur Senior" : "Chef de Projet";
            }
            if (fName.includes("department") || fLabel.includes("département")) {
                return index === 1 ? "Informatique" : "Ressources Humaines";
            }
            if (fName.includes("manager") || fLabel.includes("responsable")) {
                return index === 1 ? "Marc Martin" : "Sophie Bernard";
            }
            if (fName.includes("nationality") || fName.includes("country")) {
                return index === 1 ? "France" : "Sénégal";
            }
            if (fName.includes("location") || fName.includes("place")) {
                return index === 1 ? "Paris" : "Lyon";
            }
            if (fName.includes("ssnid") || fName.includes("secu")) {
                return index === 1 ? "1900575123456" : "2920875654321";
            }
            if (fName.includes("passport")) {
                return index === 1 ? "12AA34567" : "98ZZ65432";
            }
            if (fName.includes("bank") || fName.includes("banque")) {
                return index === 1 ? "BNP Paribas" : "Société Générale";
            }
            if (fName.includes("account") || fName.includes("iban")) {
                return index === 1 ? "FR7630004012345678901234567" : "FR7630004098765432109876543";
            }
            if (fName.includes("street") || fName.includes("address")) {
                return index === 1 ? "12 Rue de la Paix, 75002 Paris" : "45 Avenue Victor Hugo, 69002 Lyon";
            }

            if (fType === "date" || fName.includes("date") || fName.includes("birthday")) {
                if (fName.includes("birth")) {
                    return index === 1 ? "15/05/1990" : "20/11/1985";
                }
                if (fName.includes("end")) {
                    return index === 1 ? "" : "31/12/2024";
                }
                return index === 1 ? "01/01/2024" : "01/06/2024";
            }
            if (fType === "datetime") {
                return index === 1 ? "01/01/2024 09:00:00" : "01/06/2024 09:00:00";
            }

            if (fType === "float" || fType === "monetary" || fName.includes("wage") || fName.includes("salary") || fName.includes("valeur") || fName.includes("amount") || fName.includes("premium")) {
                return index === 1 ? 3500.00 : 2800.00;
            }
            if (fType === "integer" || fName.includes("children") || fName.includes("duration") || fName.includes("hours") || fName.includes("worked")) {
                return index === 1 ? 2 : 1;
            }

            if (fType === "many2one") {
                if (fName === "contract_type_id") {
                    return index === 1 ? "CDI" : "CDD";
                }
                if (fName === "employee_id" || fName.includes("employee")) {
                    return index === 1 ? "EMP001" : "Marie Martin";
                }
                return index === 1 ? "Exemple 1" : "Exemple 2";
            }

            return index === 1 ? "Exemple 1" : "Exemple 2";
        };

        const row1 = {};
        const row2 = {};

        fields.forEach(fieldObj => {
            const headerName = typeof fieldObj === 'object' ? (fieldObj.string || fieldObj.name) : fieldObj;
            row1[headerName] = getDemoValue(fieldObj, 1);
            row2[headerName] = getDemoValue(fieldObj, 2);
        });

        return [row1, row2];
    };

    const getServerFields = async () => {
        try {
            const response = await apiService.getElement(`/fields/${model_name}`);
            const fields = response.data?.fields || response.data?.result?.fields || [];
            setServerFields(fields);
            return fields;
        } catch (err) {
            notify(err.message || t('importExportButtons.fetchFieldsError'), null, 3);
            return [];
        }
    };

    const openExportModal = () => {
        getServerFields();
        setShowExportModal(true);
    };

    // NOUVEAU : Fonction pour générer le modèle avec exemples
    const handleDownloadTemplate = async () => {
        let fieldsToUse = serverFields;
        if (!fieldsToUse || fieldsToUse.length === 0) {
            fieldsToUse = await getServerFields();
        }

        if (!fieldsToUse || fieldsToUse.length === 0) {
            notify(t('importExportButtons.fetchFieldsError'), null, 3);
            return;
        }

        const headers = fieldsToUse.map(f => typeof f === 'object' ? (f.string || f.name) : f);
        const sampleRows = generateDemoRows(fieldsToUse, model_name);

        const ws = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Modèle Import");
        
        // Ajuster la largeur des colonnes pour plus de lisibilité
        const wscols = headers.map(h => ({ wch: Math.max(h.length + 5, 18) }));
        ws['!cols'] = wscols;

        XLSX.writeFile(wb, `modele_import_${model_name}.xlsx`);
    };

    const buttonBase =
        "flex items-center gap-2 rounded-md transition-colors duration-150 px-4 py-2 font-medium focus:outline-none focus:ring-2 focus:ring-blue-400";
    const buttonExport =
        "bg-green-50 border border-green-400 text-green-700 hover:bg-green-100 hover:border-green-600";
    const buttonImport =
        "bg-blue-50 border border-blue-400 text-blue-700 hover:bg-blue-100 hover:border-blue-600";

    return (
        <div className={dropdown ? "flex flex-col gap-2 w-full" : "flex gap-2"}>
            <div className="flex gap-2 w-full">
                <button
                    type="button"
                    className={`${buttonBase} ${buttonImport} ${dropdown ? "text-sm w-full" : ""}`}
                    onClick={handleImportClick}
                    disabled={importing}
                    aria-label={t('importMappingModal.importAction')}
                >
                    {importing ? (
                        <FaSpinner className="animate-spin" />
                    ) : (
                        <FaFileImport />
                    )}
                    {t('importMappingModal.importAction')}
                </button>
                <input
                    type="file"
                    ref={fileInputRef}
                    accept={accept}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                />
                <button
                    className={`${buttonBase} ${buttonExport} ${dropdown ? "text-sm w-full" : ""}`}
                    disabled={loading}
                    onClick={openExportModal}
                    aria-label={t('exportModal.export')}
                >
                    {loading ? (
                        <FaSpinner className="animate-spin" />
                    ) : (
                        <FaFileCsv />
                    )}
                    {t('exportModal.export')}
                </button>
            </div>
            {dropdown && (
                <div className="text-center mt-1">
                    <p className="text-[10px] text-gray-500 mb-0.5 leading-tight">{t('importExportButtons.useTemplateHint')}</p>
                    <button
                        type="button"
                        onClick={handleDownloadTemplate}
                        className="text-xs text-blue-600 hover:underline font-medium"
                    >
                        {t('importExportButtons.downloadTemplate')}
                    </button>
                </div>
            )}

            {showExportModal && (
                <ExportModal
                    fields={serverFields}
                    onExport={handleExport}
                    onCancel={() => setShowExportModal(false)}
                    loading={loading}
                    count={ids ? ids.length : 0}
                />
            )}

            {/* NOUVEAU : Modale de sélection de feuille */}
            {showSheetSelection && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 min-w-[350px] flex flex-col gap-4">
                        <h3 className="text-xl font-bold text-gray-800">{t('importExportButtons.chooseSheet')}</h3>
                        <p className="text-sm text-gray-600">{t('importExportButtons.multipleSheetHint')}</p>
                        
                        <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                            {sheetNames.map(name => (
                                <button 
                                    key={name} 
                                    onClick={() => handleSheetSelect(name)}
                                    className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors flex items-center justify-between group"
                                >
                                    <span className="font-medium group-hover:text-blue-700">{name}</span>
                                    <span className="text-gray-400 text-sm">➔</span>
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => {
                                setShowSheetSelection(false);
                                setCurrentWorkbook(null);
                                setSheetNames([]);
                            }}
                            className="mt-2 text-gray-500 hover:text-gray-700 text-sm font-medium"
                        >
                            {t('importExportButtons.cancelImport')}
                        </button>
                    </div>
                </div>
            )}

            {showMapping && (
                <ImportMappingModal
                    columns={fileColumns}
                    modelFields={serverFields}
                    onDownloadTemplate={handleDownloadTemplate}
                    loading={isSubmittingMapping} // PASSAGE DU LOADING
                    onConfirm={async (mapping) => {
                        setIsSubmittingMapping(true); // START LOADING
                        const mappedData = fileData.map(row => {
                            const obj = {};
                            Object.entries(mapping).forEach(([col, field]) => {
                                if (field) obj[field] = row[col];
                            });
                            return obj;
                        });
                        try {
                            const res = await apiService.saveElement(importUrl, {"records":mappedData});
                            const responseData = res.data?.result?.result || res.data?.result || {};
                            if (responseData.success === false || responseData.error || responseData.errors || res.data?.error) {
                                const errorMsg = res.data?.error?.data?.message || responseData.errors || responseData.error || responseData.message || responseData.details || t('importExportButtons.importError');
                                setImportErrors(errorMsg);
                                notify(t('importExportButtons.importError'), null, 3);
                            } else {
                                setImportErrors(null);
                                onImportSuccess && onImportSuccess();
                                notify(t('importExportButtons.importSuccess'), null, 2);
                                setShowMapping(false);
                                setFileData(null);
                                setFileColumns([]);
                            }
                        } catch (err) {
                            setImportErrors(err.message || t('importExportButtons.importError'));
                            notify(err.message || t('importExportButtons.criticalImportError'), null, 3);
                        } finally {
                            setIsSubmittingMapping(false); // STOP LOADING
                        }
                    }}
                    onTest={async (mapping) => {
                        setIsSubmittingMapping(true); // START LOADING
                        const mappedData = fileData.map(row => {
                            const obj = {};
                            Object.entries(mapping).forEach(([col, field]) => {
                                if (field) obj[field] = row[col];
                            });
                            return obj;
                        });
                        try {
                            const res = await apiService.saveElement(importUrl, {"records":mappedData, "test": true});
                            const responseData = res.data?.result?.result || res.data?.result || {};
                            if (responseData.success === false || responseData.error || responseData.errors || res.data?.error) {
                                const errorMsg = res.data?.error?.data?.message || responseData.errors || responseData.error || responseData.message || responseData.details || t('importExportButtons.testError');
                                setImportErrors(errorMsg);
                                notify(t('importExportButtons.testRevealedErrors'), null, 4);
                            } else {
                                setImportErrors(null);
                                notify(t('importExportButtons.testSuccess'), null, 2);
                            }
                        } catch (err) {
                            setImportErrors(err.message || t('importExportButtons.testError'));
                            notify(err.message || t('importExportButtons.technicalTestError'), null, 3);
                        } finally {
                            setIsSubmittingMapping(false); // STOP LOADING
                        }
                    }}
                    onCancel={() => {
                        setShowMapping(false);
                        setFileData(null);
                        setFileColumns([]);
                        setImportErrors(null);
                    }}
                    errors={importErrors}

                />
            )}
        </div>
    );
}

export default ImportExportButtons;